import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import { TReportRefundsDistributionByCustomerCountryData } from '@src/types';
import {
  exportRefundsDistributionByCustomerCountryReport,
  getRefundsDistributionByCustomerCountryReport,
} from '@src/services/reports';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { useCountryOptions } from '@src/hooks/useCountryOptions';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportChartData } from '@hooks/useReportChartData';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportRefundsDistributionByCustomerCountryData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          ${payload[0]?.value}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.country}
        </p>
        <p className="text-white text-xs">
          Refund Amount: ${payload[0]?.value}
        </p>
      </div>
    );
  }

  return null;
};

export const RefundsDistributionByCustomerCountry: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<
    TReportRefundsDistributionByCustomerCountryData[]
  >([]);
  const { countryOptions } = useCountryOptions();

  const filters = useReportFilters({
    enableCountries: true,
  });

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportRefundsDistributionByCustomerCountryData>({
      data,
      getAccessField: (item) => item.country,
      getMainDataField: (item, key) => {
        if (key === 'amount') return item.amount;

        return 0;
      },
      includeTotalsRow: true,
      customColumns: [
        {
          title: 'AMOUNT',
          className: 'text-right',
          render: (item) => <p className="text-white">${item?.amount ?? 0}</p>,
        },
      ],
    });

  // Use custom hook for chart data
  const chartData = useReportChartData({
    data,
    displayItems: ['amount'],
    getAccessField: (item) => item.country,
    getMainDataField: (item, key) => {
      if (key === 'amount') return item.amount;

      return 0;
    },
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const response = await getRefundsDistributionByCustomerCountryReport({
        countries:
          filters.selectedCountries.length > 0 ? filters.selectedCountries : [],
      });

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error fetching report');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await exportRefundsDistributionByCustomerCountryReport({
        countries:
          filters.selectedCountries.length > 0 ? filters.selectedCountries : [],
      });

      downloadFile(response.data, 'refunds-distribution-by-customer-country');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Refunds Distribution by Customer Country
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCountriesFilter
          countryOptions={countryOptions}
          isLoading={filters.isLoading}
          selectedCountries={filters.selectedCountries}
          showGroupByFilter={false}
          showPeriodFilter={false}
          onCountriesChange={filters.setSelectedCountries}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="country"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                barSize={60}
                dataKey="amount"
                fill="#B6D72C"
                name="Refund Amount"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
